import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Input } from '../../components/ui/Input'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { EmptyState } from '../../components/ui/EmptyState'
import { integrationsApi } from '../../api/integrations.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import type { ApiKey, CreatedApiKey } from '../../api/types'

function NewKeyReveal({ created, onDismiss }: { created: CreatedApiKey; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(created.token)
    setCopied(true)
  }

  return (
    <Card style={{ borderColor: 'var(--warning)' }}>
      <h3 style={{ marginTop: 0 }}>API-Key "{created.name}" erstellt</h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
        Dieses Token wird nur jetzt angezeigt und kann danach nicht mehr abgerufen werden — jetzt kopieren und sicher
        aufbewahren.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <code style={{ flex: 1, padding: 'var(--space-2)', background: 'var(--surface-2)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', overflowX: 'auto', whiteSpace: 'nowrap' }}>
          {created.token}
        </code>
        <Button size="sm" onClick={copy}>
          {copied ? 'Kopiert ✓' : 'Kopieren'}
        </Button>
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Fertig
        </Button>
      </div>
    </Card>
  )
}

function ApiKeyRow({ apiKey, onRevoke }: { apiKey: ApiKey; onRevoke: (id: string) => void }) {
  const isRevoked = Boolean(apiKey.revokedAt)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--surface-border)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <strong>{apiKey.name}</strong>
          <Badge variant={isRevoked ? 'neutral' : 'success'} dot>
            {isRevoked ? 'Widerrufen' : 'Aktiv'}
          </Badge>
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
          Scopes: {apiKey.scopes.join(', ')} · erstellt von {apiKey.creator.displayName} am{' '}
          {new Date(apiKey.createdAt).toLocaleDateString('de-DE')}
          {apiKey.lastUsedAt ? ` · zuletzt genutzt ${new Date(apiKey.lastUsedAt).toLocaleString('de-DE')}` : ' · noch nie genutzt'}
        </div>
      </div>
      {!isRevoked ? (
        <Button size="sm" variant="danger" onClick={() => onRevoke(apiKey.id)}>
          Widerrufen
        </Button>
      ) : null}
    </div>
  )
}

export function IntegrationsSettingsPanel() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState('')
  const [revokeId, setRevokeId] = useState<string | null>(null)
  const [justCreated, setJustCreated] = useState<CreatedApiKey | null>(null)

  const keysQuery = useQuery({ queryKey: ['integrations', 'api-keys'], queryFn: async () => (await integrationsApi.listApiKeys()).data })

  const createMutation = useMutation({
    mutationFn: () => integrationsApi.createApiKey({ name, scopes: scopes.split(',').map((s) => s.trim()).filter(Boolean) }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'api-keys'] })
      setJustCreated(res.data)
      setName('')
      setScopes('')
      toast.success('API-Key erstellt.')
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Erstellen fehlgeschlagen.'),
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => integrationsApi.revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'api-keys'] })
      toast.success('API-Key widerrufen.')
      setRevokeId(null)
    },
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: 720 }}>
      <Card>
        <h3 style={{ marginTop: 0 }}>API-Keys</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
          Maschinelle Zugänge für zukünftige externe Integrationen (z.B. eine spätere FiveM-Serveranbindung). Es gibt
          aktuell noch keine konkreten Integrations-Endpunkte — dies verwaltet nur die Zugangsdaten dafür.
        </p>

        {keysQuery.data && keysQuery.data.length > 0 ? (
          <div>
            {keysQuery.data.map((k) => (
              <ApiKeyRow key={k.id} apiKey={k} onRevoke={setRevokeId} />
            ))}
          </div>
        ) : (
          <EmptyState icon="🔑" title="Noch keine API-Keys" description="Lege einen API-Key für eine zukünftige Integration an." />
        )}
      </Card>

      {justCreated ? <NewKeyReveal created={justCreated} onDismiss={() => setJustCreated(null)} /> : null}

      <Card>
        <h3 style={{ marginTop: 0 }}>Neuer API-Key</h3>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Name" htmlFor="apikey-name">
              <Input id="apikey-name" placeholder="z.B. FiveM-Server" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Scopes (kommagetrennt)" htmlFor="apikey-scopes">
              <Input id="apikey-scopes" placeholder="z.B. tasks.read, roadmap.read" value={scopes} onChange={(e) => setScopes(e.target.value)} />
            </Field>
          </div>
          <Button
            disabled={!name.trim() || !scopes.trim()}
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Erstellen
          </Button>
        </div>
      </Card>

      {revokeId ? (
        <ConfirmDialog
          title="API-Key widerrufen"
          message="Dieser API-Key kann danach nicht mehr verwendet werden. Diese Aktion kann nicht rückgängig gemacht werden."
          danger
          loading={revokeMutation.isPending}
          onConfirm={() => revokeMutation.mutate(revokeId)}
          onCancel={() => setRevokeId(null)}
        />
      ) : null}
    </div>
  )
}
